import { CustomModal } from "components/custom-modal";
import { useEffect, useState } from "react";
import { makeEvent, useEventListener } from "services/events";
import { EquipeVendaRowProps } from "../utils";
import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Switch,
  Text,
} from "@chakra-ui/react";
import { InputField } from "components/input-field";
import { Checkbox } from "components/checkbox";
import { getUsers } from "components/atendimentos-components/whatsapp-attendances/components/attendances-list/functions";
import { CloseIcon } from "components/vectors/close-icon";
import { AddIcon } from "@chakra-ui/icons";
import { handleSubmitEquipes } from "./utils";
import { ErrorProps } from "utils/object-methods";
import { useApplicationContext } from "contexts/ApplicationContext";
import { ErrorBox } from "components/error-box";

export interface ModalEquipeParams {
  row: EquipeVendaRowProps;
}
export interface EquipeProps extends EquipeVendaRowProps {}

export function ModalEquipes({
  onUpdate,
  users,
}: {
  onUpdate: () => void;
  users: { name: string; value: number; isVendeAI: boolean }[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [equipe, setEquipe] = useState<Partial<EquipeProps>>({
    enabled: true,
  });
  const [errors, setErrors] = useState<ErrorProps<EquipeProps>>({});
  const [modalParams, setModalParams] = useState<Partial<ModalEquipeParams>>(
    {}
  );

  const [loading, setLoading] = useState(false);
  const { user } = useApplicationContext();
  const isUpdate = !!modalParams.row;

  const onOpen = (props?: ModalEquipeParams) => {
    setIsOpen(true);
    if (props?.row) {
      setModalParams(props);
      setEquipe({ ...props.row });
    }
  };
  const onClose = () => {
    setIsOpen(false);
    setEquipe({ enabled: true });
    setModalParams({});
    setErrors({});
  };

  const onChangeField = (key: keyof EquipeProps, value: any) => {
    setEquipe((prev) => ({ ...prev, [key]: value }));
    setErrors((e) => {
      delete e[key];
      return { ...e };
    });
  };

  const onChangeMultiselect = (field: "usersIds", value: any) => {
    const currentEl = equipe[field] as any[];

    if (currentEl?.length && currentEl != null) {
      const isInclude = currentEl.includes(value);
      if (isInclude)
        onChangeField(
          field,
          currentEl.filter((curr: any) => curr !== value)
        );
      else onChangeField(field, [...equipe[field]!, value]);
    } else onChangeField(field, [value]);
  };

  useEventListener("openModalEquipes", onOpen);

  const modalFooter = (
    <>
      <Button
        onClick={() =>
          handleSubmitEquipes({
            isUpdate,
            setLoading,
            equipe,
            setErrors,
            onClose: () => {
              onClose();
              onUpdate();
            },
            customerId: user.userData.customerId!,
          })
        }
        leftIcon={<AddIcon w="15px" h="15px" />}
        isLoading={loading}
        loadingText={isUpdate ? "Salvando" : "Criando"}
      >
        {isUpdate ? "Salvar" : "Criar"}
      </Button>
      <Button variant="outline" onClick={onClose} leftIcon={<CloseIcon />}>
        Cancelar
      </Button>
    </>
  );

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      modalTitle={isUpdate ? "Editar Equipe" : "Criar Equipe"}
      size="3xl"
      scroll="inside"
      modalFooter={modalFooter}
    >
      <Grid templateColumns="1fr 150px" gap="15px" mb="15px">
        <Box>
          <InputField
            title="Nome da equipe"
            value={equipe.nome ?? ""}
            onChange={({ target: { value } }) => onChangeField("nome", value)}
            errorMessage={errors.nome}
          />
        </Box>
        <Box>
          <Text fontSize="14px" fontWeight="medium" mb="8px">
            Status da equipe
          </Text>
          <Flex alignItems="center" h="32px">
            <Switch
              onChange={(e) => onChangeField("enabled", e.target.checked)}
              isChecked={equipe.enabled}
              colorScheme="green"
            />
            <Text
              ml="5px"
              fontWeight="medium"
              color={equipe.enabled ? "green.500" : "gray.400"}
            >
              {equipe.enabled ? "Ativo" : "Inativo"}
            </Text>
          </Flex>
        </Box>
      </Grid>
      <Flex
        justifyContent="space-between"
        w="100%"
        mb="8px"
        alignItems="center"
      >
        <Text fontSize="14px" fontWeight="medium">
          Selecionar Vendedores
        </Text>
        {users.length === equipe.usersIds?.length ? (
          <Text
            fontSize="14px"
            fontWeight="medium"
            color="#008"
            onClick={() => setEquipe((prev) => ({ ...prev, usersIds: [] }))}
            cursor="pointer"
          >
            Desselecionar Todos
          </Text>
        ) : (
          <Text
            fontSize="14px"
            fontWeight="medium"
            color="#008"
            onClick={() =>
              setEquipe((prev) => ({
                ...prev,
                usersIds: users.map(({ value }) => value),
              }))
            }
            cursor="pointer"
          >
            Selecionar Todos
          </Text>
        )}
      </Flex>
      <ErrorBox
        p="10px"
        gap="8px"
        border="1px solid var(--chakra-colors-custom-gray)"
        borderRadius="6px"
        gridTemplateColumns="1fr 1fr"
        overflow="auto"
        maxH="250px"
        errorMessage={errors["usersIds"]}
        mb="15px"
        display="grid"
      >
        {users.map((user, i) => {
          return (
            <Flex alignItems="center" key={i}>
              <Checkbox
                onChange={() => onChangeMultiselect("usersIds", user.value)}
                isChecked={equipe.usersIds?.includes(user.value)}
              >
                {user.name}
              </Checkbox>
            </Flex>
          );
        })}
      </ErrorBox>
      {equipe.usersIds?.length ? (
        <Text color="gray.400" fontSize="12px" mt="8px">
          {equipe.usersIds?.length} vendedor(es) selecionado(s)
        </Text>
      ) : null}
    </CustomModal>
  );
}

export const openModalEquipes = (props?: ModalEquipeParams) =>
  makeEvent("openModalEquipes", props);
